import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { IonicAuth, IonicAuthOptions } from '@ionic-enterprise/auth';
import { NavController } from '@ionic/angular';
import { Capacitor } from '@capacitor/core';

// services
import { OmniIdentityService } from '../../services/omni-identity.service';

// Models
import { ICognitoUser } from '../../models/utilities/cognito-user.model';
import { IAdminSettings } from '../../models/utilities/admin-settings.model';
import { EnvironmentData } from 'src/environments/environment-data';
import { environment } from '../../../environments/environment';
import { IappClient } from '../../../environments/environment-data';
import * as Sentry from '@sentry/capacitor';

type CurrentPlatform = 'browser' | 'native';

@Injectable({
  providedIn: 'root',
})
export class AuthConnectProvider {
  public static readonly ADMIN_SETTINGS = 'adminSettings';
  public static readonly USER_TOKENS = 'userToken';
  public static readonly SETTINGS_VERSION = '3';

  private currentPlatform: CurrentPlatform;
  private isIosWebView: any;
  private isPlatform: any;
  private redirectUri: any;
  private logoutUri: any;
  private authConnect: IonicAuth;
  private lastParsedJWTBody: any;

  constructor(private omniService: OmniIdentityService, private navCtrl: NavController) {
    const cognitoConfig: IonicAuthOptions = {
      authConfig: 'cognito', // The auth provider.
      platform: 'web', // The platform which the app is running on
      // webAuthFlow: 'PKCE',
      implicitLogin: 'CURRENT',
      clientID: '[ CLIENT TO CONNECT TO USER POOL ]',
      discoveryUrl: `[ URL TO AWS COGNITO OAUTH FLOW]`,
      redirectUri: '[ URL TO SUCCESS REDIRECT ]',
      // requested scopes from provider
      // scope: 'FILL_IN',
      // the audience, if applicable
      // audience: 'FILL_IN',
      logoutUrl: '[ URL TO LOGOUT REDIRECT ]',
      // The type of iOS webview to use. 'shared' will use a webview that can
      // share session/cookies on iOS to provide SSO across multiple apps but
      // will cause a prompt for the user which asks them to confirm they want
      // to share site data with the app. 'private' uses a webview which will
      // not prompt the user but will not be able to share session/cookie data
      // either for true SSO across multiple apps.
      iosWebView: 'private',
      // required if running on the Web
      // clientSecret: 'FILL_IN'
    };
    this.getCurrentPlatform();
    this.authConnect = new IonicAuth(cognitoConfig);
  }

  public configureAuthConnect(
    clientID: string,
    loginUrl: string,
    logoutUrl: string,
    region: string,
    implicitLogin: 'POPUP' | 'CURRENT'
  ): void {
    // set the right config variables for auth by detecting which platform the app is running
    this.setRightDataForAuth(loginUrl, logoutUrl);
    if (!EnvironmentData.getEnvironmentSwitcher()) {
      EnvironmentData.setEnvironmentSwitcher(environment.mode);
    }
    // const userPool = EnvironmentData.getUserPoolByAppClient( this.environmentProvider.currentModeType, clientID);
    const userPool = EnvironmentData.getUserPoolByRegion(region);
    if (!userPool) {
      this.deleteSettings();
      this.navCtrl.navigateForward('/admin-setup');
      return;
    }
    const webAuthFlow = 'PKCE';
    const cognitoConfig: IonicAuthOptions = {
      authConfig: 'cognito',
      platform: this.isPlatform,
      implicitLogin,
      clientID,
      scope: 'openid',
      discoveryUrl: `https://cognito-idp.${userPool.region}.amazonaws.com/${userPool.userPoolId}/.well-known/openid-configuration`,
      redirectUri: this.redirectUri,
      logoutUrl: this.logoutUri,
      iosWebView: this.isIosWebView,
      webAuthFlow,
    };
    this.authConnect = new IonicAuth(cognitoConfig);
  }

  public configureAuthConnectOnlyUrl(loginUrl: string, logoutUrl: string): void {
    this.setRightDataForAuth(loginUrl, logoutUrl);
    if (!EnvironmentData.getEnvironmentSwitcher()) {
      EnvironmentData.setEnvironmentSwitcher(environment.mode);
    }

    // const userPool = EnvironmentData.getUserPool( this.environmentProvider.currentModeType );
    const appClientIdStored = EnvironmentData.getAppClientId();
    const userPool = EnvironmentData.getUserPool();
    let appClientValue: IappClient;
    if (!!appClientIdStored) {
      appClientValue = EnvironmentData.getAppClientSettings();
    } else {
      if (userPool) {
        appClientValue = userPool.appClients[0];
      }
    }
    if (!!appClientValue) {
      const cognitoConfig: IonicAuthOptions = {
        authConfig: 'cognito',
        platform: this.isPlatform,
        implicitLogin: 'POPUP',
        clientID: appClientValue.id,
        discoveryUrl: `https://cognito-idp.${userPool.region}.amazonaws.com/${userPool.userPoolId}/.well-known/openid-configuration`,
        redirectUri: this.redirectUri,
        logoutUrl: this.logoutUri,
        iosWebView: this.isIosWebView,
      };
      this.authConnect = new IonicAuth(cognitoConfig);
    }
  }

  public async isAuthenticated(): Promise<boolean> {
    return await this.authConnect
      .isAuthenticated()
      .then((res) => res)
      .catch((error) => {
        console.error('NOT AUTH', error);
        return false;
      });
  }

  public async userIsSSO(): Promise<boolean> {
    if (!this.lastParsedJWTBody) {
      const token = await this.getIdToken();
      this.parseJWTBody(token);
    }

    const parsedJWTBody = this.lastParsedJWTBody;

    return !!parsedJWTBody.identities?.at(0)?.issuer;
  }

  public async handleCallback(): Promise<void> {
    await this.authConnect
      .handleCallback(window.location.href)
      .then()
      .catch(async (err) => {
        console.error('Error Handle Callback', err);
      });
  }

  public async isExpired(): Promise<any> {
    return await this.authConnect
      .isAccessTokenExpired()
      .then((res) => res)
      .catch((err) => {
        console.error('Expired Error', err);
      });
  }

  public async refreshToken(): Promise<void> {
    const test = await this.authConnect
      .refreshSession()
      .then()
      .catch((err) => {
        console.error('Error at refreshing', err);
      });
  }

  public async isRefreshAvailable(): Promise<void> {
    await this.authConnect
      .isRefreshTokenAvailable()
      .then((res) => {
        console.log('Is Available Refresh', res);
      })
      .catch((err) => {
        console.error('Error Is Available', err);
      });
  }

  public async expireToken(): Promise<void> {
    await this.authConnect
      .expire()
      .then(() => {
        console.log('Expiring Token...');
      })
      .catch((err) => {
        console.error('Expire Error', err);
      });
  }

  public async getAccessToken(): Promise<string> {
    try {
      return await this.authConnect.getAccessToken();
    } catch (e) {
      console.error('Get Access Token err', e.message);
    }
  }

  public async getIdToken(): Promise<string> {
    try {
      return await this.authConnect.getAuthResponse().then((payload) => payload.id_token);
    } catch (e) {
      console.error('Get ID Token err', e.message);
    }
  }

  public async getRefreshToken(): Promise<string> {
    try {
      return await this.authConnect.getRefreshToken();
    } catch (e) {
      console.error('Get Refresh Token err', e.message);
    }
  }

  public async getOrganizationID(lookup: string): Promise<string> {
    try {
      const idToken = await this.getIdToken();
      let organizationId: string;
      if (idToken && idToken !== null) {
        await this.omniService
          .getOrganization(idToken, lookup)
          .then((res: any) => {
            console.log('Omni', res);
            if (res.data.length > 0) {
              organizationId = res.data[0].id;
            } else {
              organizationId = null;
            }
          })
          .catch((error: any) => {
            console.log('Ommni err', error);
            organizationId = error.error.title;
          });
      } else {
        organizationId = null;
      }
      return organizationId;
    } catch (e) {
      console.error('Get ID Token err', e.message);
      return null;
    }
  }

  public async getCognitoUserAsync(): Promise<ICognitoUser> {
    if (await this.isAuthenticated()) {
      const authResponse = await this.authConnect.getAuthResponse();
      return this.createCognitoUser(
        authResponse.access_token,
        authResponse.id_token,
        authResponse.refresh_token,
        authResponse.expires_in
      );
    }
  }

  public async loginUser(): Promise<void> {
    // Tries to signIn, on succes will continue the next and return a cognito user
    //onLoginSuccess()
    await this.authConnect
      .login()
      .then()
      .catch((error) => {
        Sentry.captureException(error);
        throw new Error(error);
      });
  }

  public async logoutUser(flag = false): Promise<void> {
    // Tries to signOut, on succes will continue the next
    // There's a bug that is not deleting correctly the localstorage and remains the class as authenticated
    // This is a temporary solution
    const keysLength = localStorage.length;
    const authConnectKeys = [];
    for (let i = 0; i < keysLength; i++) {
      if (localStorage.key(i).includes('_ionicAuth')) {
        authConnectKeys.push(localStorage.key(i));
      }
    }
    authConnectKeys.forEach((key) => {
      localStorage.removeItem(key);
    });

    await this.authConnect
      .logout()
      .then(() => {
        if (this.currentPlatform === 'native') {
          if (flag) {
            this.reload();
          }
        }
      })
      .catch((error) => {
        console.error('Error at logging out', error);
      });
  }

  // This will create a simplifed version of the cognitoUser model that returns amplify
  private createCognitoUser(accessToken: string, idToken: string, refreshToken: string, expiresIn: any): ICognitoUser {
    return {
      cognitoUserSession: {
        accessToken: accessToken || '',
        idToken: idToken || '',
        refreshToken: refreshToken || '',
        expiresIn: expiresIn || '',
      },
    };
  }

  // Verify that the settings are correct and will connect to a user pool
  // For now only validates that the settings are alredy on local storage
  public verifySettings(): boolean {
    const settings = JSON.parse(localStorage.getItem(AuthConnectProvider.ADMIN_SETTINGS));
    return (
      !!settings &&
      settings !== undefined &&
      settings !== null &&
      settings.clientdId !== '' &&
      settings.country !== '' &&
      settings.organizationId != null
    );
  }

  public verifySettingsVersioning(): boolean {
    const settings = JSON.parse(localStorage.getItem(AuthConnectProvider.ADMIN_SETTINGS));
    if (settings.version === null || settings.version !== AuthConnectProvider.SETTINGS_VERSION) {
      EnvironmentData.setEnvironmentSwitcher(environment.mode);
      this.deleteSettings();
      return false;
    }
    return true;
  }

  public saveSettings(settings: IAdminSettings): void {
    localStorage.setItem(AuthConnectProvider.ADMIN_SETTINGS, JSON.stringify(settings));
  }

  public deleteSettings(): void {
    localStorage.removeItem('ENVS');
    localStorage.removeItem(AuthConnectProvider.ADMIN_SETTINGS);
    EnvironmentData.deleteCurrentEndPoints();
  }

  public deleteAdminSettings(): void {
    localStorage.removeItem(AuthConnectProvider.ADMIN_SETTINGS);
    EnvironmentData.deleteCurrentEndPoints();
  }

  public static getSettings(): IAdminSettings {
    const settings = localStorage.getItem(this.ADMIN_SETTINGS);
    return JSON.parse(settings);
  }

  public countryCatalog(): Observable<any> {
    return of([
      { id: 'us-east-1', name: 'United States of America' },
      // { id: 'us-west-1', name: 'Australia' }
    ]);
  }

  public static cleanUrl(): string {
    let href = window.location.href;
    const indexParam = href.indexOf('?');
    const indexHash = href.indexOf('#');
    if (indexParam > 0) {
      href = href.slice(0, indexParam);
    }
    if (indexHash > 0) {
      href = href.slice(0, indexHash);
    }
    return href;
  }

  public static cleanUrlLogin(): string {
    let href = AuthConnectProvider.cleanUrl();
    const tabsParam = href.indexOf('/tabs');
    const detailsParam = href.indexOf('/cart-details');
    if (tabsParam > 0) {
      href = href.slice(0, tabsParam);
    }
    if (detailsParam > 0) {
      href = href.slice(0, detailsParam);
    }
    return `${href}/login`;
  }

  private getCurrentPlatform() {
    if (
      Capacitor.getPlatform() === 'ios' ||
      (Capacitor.getPlatform() === 'android' &&
        (Capacitor.getPlatform() !== 'desktop' || Capacitor.getPlatform() === 'mobileweb'))
    ) {
      //native
      this.currentPlatform = 'native';
    } else {
      //browser
      this.currentPlatform = 'browser';
    }
  }

  private setRightDataForAuth(loginUrl: string, logoutUrl: string) {
    this.isPlatform = 'web';
    this.isIosWebView = 'private';
    this.redirectUri = loginUrl;
    this.logoutUri = logoutUrl;
    if (this.currentPlatform === 'native') {
      this.redirectUri = 'com.cbord.traylogistics://login';
      this.logoutUri = 'com.cbord.traylogistics://login'; // com.company.app://login?logout=true.
      this.isPlatform = 'capacitor';
      this.isIosWebView = 'shared';
    }
  }

  public parseJWTBody<T = any>(token: string): T {
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split('')
        .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join('')
    );
    const parsedToken = JSON.parse(jsonPayload);

    this.lastParsedJWTBody = parsedToken;
    return parsedToken;
  }

  reload(): void {
    window.location.reload();
  }
}
